/* eslint-disable max-lines */
import { Chips, Input, InputAdornment, NumberInput, Textarea, useToast } from '@cian/ui-kit';
import { getWebview } from '@cian/webview/webview';
import { ChangeEvent, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Action } from 'redux';
import { DobroForm } from 'shared/components/DobroForm';
import { DobroFormField } from 'shared/components/DobroFormField';
import { DobroFormSign } from 'shared/components/DobroFormSign';
import { DobroFormSuccess } from 'shared/components/DobroFormSuccess';
import { useAppDispatch } from 'shared/hooks';
import {
  IFormState,
  setAdditionalPhone,
  setAddress,
  setArea,
  setAvailableFor,
  setAvailablePeriod,
  setBedsCount,
  setBedsDescription,
  setFacilities,
  setFloor,
  setGuestsCount,
  setLocation,
  setMoscow,
  setName,
  setObjectType,
  setOfferSigned,
  setOtherWishes,
  setPhone,
  setPriceDescription,
  setPriceType,
  setRoomsCount,
  setTelegram,
} from 'shared/slices/formSlice';
import { submitForm } from 'shared/thunks/submitForm/submitForm';
import { EMoscow, EObjectType, EPriceType, ERoomsCount } from 'shared/types/form';
import { TApplicationState } from 'shared/types/redux';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { formatObjectType, formatPriceType, formatRoomsCount, getAreaLabel } from 'shared/utils/formatters';
import { locationVisible, priceDescriptionVisible, studioValueVisible } from 'shared/utils/dobroForm/visible';
import { PhoneInput } from 'shared/components/PhoneInput';

function getRoomsCountOptions(formState: IFormState) {
  const roomsCountOptions = [
    { label: formatRoomsCount(ERoomsCount.One), value: ERoomsCount.One },
    { label: formatRoomsCount(ERoomsCount.Two), value: ERoomsCount.Two },
    { label: formatRoomsCount(ERoomsCount.Three), value: ERoomsCount.Three },
    { label: formatRoomsCount(ERoomsCount.Four), value: ERoomsCount.Four },
    { label: formatRoomsCount(ERoomsCount.Five), value: ERoomsCount.Five },
    { label: formatRoomsCount(ERoomsCount.Six_and_more), value: ERoomsCount.Six_and_more },
  ];

  if (studioValueVisible(formState)) {
    roomsCountOptions.unshift({ label: formatRoomsCount(ERoomsCount.Studio), value: ERoomsCount.Studio });
  }

  return roomsCountOptions;
}

function useChipsSingleCallback<T>(callback: (value: T | null) => Action) {
  const dispatch = useAppDispatch();

  return useCallback(
    (value: T[]) => {
      dispatch(callback(value[0] || /* istanbul ignore next */ null));
    },
    [dispatch, callback],
  );
}

function useChipsSingleStringCallback(callback: (value: string) => Action) {
  const dispatch = useAppDispatch();

  return useCallback(
    (value: string[]) => {
      dispatch(callback(value[0] || /* istanbul ignore next */ ''));
    },
    [dispatch, callback],
  );
}

function useChipsCallback(callback: (value: string[]) => Action) {
  const dispatch = useAppDispatch();

  return useCallback(
    (value: string[]) => {
      dispatch(callback(value));
    },
    [dispatch, callback],
  );
}

function useInputCallback(callback: (value: string) => Action) {
  const dispatch = useAppDispatch();

  return useCallback(
    (_: ChangeEvent, value: string) => {
      dispatch(callback(value));
    },
    [dispatch, callback],
  );
}

function useCheckboxCallback(callback: (value: boolean) => Action) {
  const dispatch = useAppDispatch();

  return useCallback(
    (_: ChangeEvent, value: boolean) => {
      dispatch(callback(value));
    },
    [dispatch, callback],
  );
}

function useNumberInputCallback(callback: (value: number | null) => Action) {
  const dispatch = useAppDispatch();

  return useCallback(
    (value: number | undefined) => {
      dispatch(callback(value === undefined ? /* istanbul ignore next */ null : value));
    },
    [dispatch, callback],
  );
}

function usePhoneInputCallback(callback: (value: string) => Action) {
  const dispatch = useAppDispatch();

  return useCallback(
    (value: string) => {
      dispatch(callback(value));
    },
    [dispatch, callback],
  );
}

export function DobroFormContainer() {
  const { config } = useApplicationContext();
  const formState = useSelector<TApplicationState, IFormState>(state => state.form);
  const dispatch = useAppDispatch();
  const toastApi = useToast();

  const offerDocumentUrl = config.get<string>('dobroForm.offerDocumentUrl') || 'https://www.cian.ru/';
  const politicsDocumentUrl = config.get<string>('dobroForm.politicsDocumentUrl') || 'https://www.cian.ru/';
  const isWebview = config.get<boolean>('web.isWebView') ?? /* istanbul ignore next */ false;

  const objectTypeOnChange = useChipsSingleCallback(setObjectType);
  const moscowOnChange = useChipsSingleCallback(setMoscow);
  const locationOnChange = useInputCallback(setLocation);
  const addressOnChange = useInputCallback(setAddress);
  const floorOnChange = useNumberInputCallback(setFloor);

  const roomsCountOnChange = useChipsSingleCallback(setRoomsCount);
  const areaOnChange = useNumberInputCallback(setArea);
  const guestsCountOnChange = useNumberInputCallback(setGuestsCount);
  const bedsCountOnChange = useNumberInputCallback(setBedsCount);
  const bedsDescriptionOnChange = useInputCallback(setBedsDescription);

  const facilitiesOnChange = useChipsCallback(setFacilities);

  const availablePeriodOnChange = useChipsSingleStringCallback(setAvailablePeriod);
  const priceTypeOnChange = useChipsSingleCallback(setPriceType);
  const priceDescriptionOnChange = useInputCallback(setPriceDescription);
  const availableForOnChange = useChipsCallback(setAvailableFor);
  const otherWishesOnChange = useInputCallback(setOtherWishes);

  const nameOnChange = useInputCallback(setName);
  const phoneOnChange = usePhoneInputCallback(setPhone);
  const additionalPhoneOnChange = usePhoneInputCallback(setAdditionalPhone);
  const telegramOnChange = useInputCallback(setTelegram);

  const offerSignedOnChange = useCheckboxCallback(setOfferSigned);

  const onSubmit = useCallback(async () => {
    try {
      await dispatch(submitForm()).unwrap();
    } catch (_) {
      toastApi.open({ type: 'error', text: 'Что-то пошло не так, попробуйте ещё раз' });
    }
  }, [dispatch, toastApi]);

  const successOnClick = useCallback(() => {
    if (isWebview) {
      getWebview().close();
    } else {
      document.location.href = '/';
    }
  }, [isWebview]);

  const onOfferDocumentClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (isWebview) {
        e.preventDefault();
        getWebview().sendAction({
          action: 'openInBrowser',
          payload: { url: offerDocumentUrl, isAuthorizationRequired: false },
        });
      }
    },
    [isWebview, offerDocumentUrl],
  );

  const onPoliticsDocumentClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (isWebview) {
        e.preventDefault();
        getWebview().sendAction({
          action: 'openInBrowser',
          payload: { url: politicsDocumentUrl, isAuthorizationRequired: false },
        });
      }
    },
    [isWebview, politicsDocumentUrl],
  );

  if (formState.saved) {
    return <DobroFormSuccess buttonOnClick={successOnClick} />;
  }

  return (
    <DobroForm
      onSubmit={onSubmit}
      loading={formState.saving}
      webview={isWebview}
      objectType={
        <DobroFormField name="DobroFormContainer-objectType" label="Тип жилья" required>
          <Chips
            invalid={formState.errors.invalidFields.includes('objectType')}
            value={formState.data.objectType ? [formState.data.objectType] : []}
            onChange={objectTypeOnChange}
            options={[
              { label: formatObjectType(EObjectType.Flat), value: EObjectType.Flat },
              { label: formatObjectType(EObjectType.Room), value: EObjectType.Room },
              { label: formatObjectType(EObjectType.House), value: EObjectType.House },
              { label: formatObjectType(EObjectType.Bed), value: EObjectType.Bed },
            ]}
          ></Chips>
        </DobroFormField>
      }
      moscow={
        <DobroFormField name="DobroFormContainer-moscow" label="Регион" required>
          <Chips
            value={formState.data.moscow ? [formState.data.moscow] : []}
            onChange={moscowOnChange}
            invalid={formState.errors.invalidFields.includes('moscow')}
            options={[
              { label: 'Москва', value: EMoscow.Moscow },
              { label: 'Московская область', value: EMoscow.MoscowRegion },
              { label: 'Другой', value: EMoscow.Other },
            ]}
          ></Chips>
        </DobroFormField>
      }
      location={
        locationVisible(formState) && (
          <DobroFormField name="DobroFormContainer-location" label="Город" required>
            <Input
              value={formState.data.location}
              onChange={locationOnChange}
              size="M"
              invalid={formState.errors.invalidFields.includes('location')}
            />
          </DobroFormField>
        )
      }
      address={
        <DobroFormField name="DobroFormContainer-address" label="Адрес" required>
          <Input
            value={formState.data.address}
            onChange={addressOnChange}
            size="M"
            invalid={formState.errors.invalidFields.includes('address')}
          />
        </DobroFormField>
      }
      floor={
        <DobroFormField name="DobroFormContainer-floor" label="Этаж" errorMessage={formState.errors.floorErrorMessage}>
          <NumberInput
            value={formState.data.floor || undefined}
            onChange={floorOnChange}
            size="M"
            invalid={formState.errors.invalidFields.includes('floor')}
          />
        </DobroFormField>
      }
      roomsCount={
        <DobroFormField name="DobroFormContainer-roomsCount" label="Количество комнат">
          <Chips
            value={formState.data.roomsCount ? [formState.data.roomsCount] : []}
            onChange={roomsCountOnChange}
            options={getRoomsCountOptions(formState)}
          ></Chips>
        </DobroFormField>
      }
      area={
        <DobroFormField
          name="DobroFormContainer-area"
          label={getAreaLabel(formState.data.objectType)}
          errorMessage={formState.errors.areaErrorMessage}
        >
          <NumberInput
            value={formState.data.area || undefined}
            onChange={areaOnChange}
            rightAdornment={<InputAdornment>м²</InputAdornment>}
            size="M"
            invalid={formState.errors.invalidFields.includes('area')}
          />
        </DobroFormField>
      }
      guestsCount={
        <DobroFormField
          name="DobroFormContainer-guestsCount"
          label="Количество гостей"
          required
          errorMessage={formState.errors.guestsCountErrorMessage}
        >
          <NumberInput
            value={formState.data.guestsCount || undefined}
            invalid={formState.errors.invalidFields.includes('guestsCount')}
            onChange={guestsCountOnChange}
            size="M"
          />
        </DobroFormField>
      }
      bedsCount={
        <DobroFormField
          name="DobroFormContainer-bedsCount"
          label="Количество спальных мест"
          errorMessage={formState.errors.bedsCountErrorMessage}
        >
          <NumberInput
            value={formState.data.bedsCount || undefined}
            onChange={bedsCountOnChange}
            size="M"
            invalid={formState.errors.invalidFields.includes('bedsCount')}
          />
        </DobroFormField>
      }
      bedsDescription={
        <DobroFormField name="DobroFormContainer-bedsDescription" label="Спальные места">
          <Input
            value={formState.data.bedsDescription}
            onChange={bedsDescriptionOnChange}
            placeholder="Например, 2-сп. кровать и диван"
            size="M"
          />
        </DobroFormField>
      }
      facilities={
        <DobroFormField name="DobroFormContainer-facilities">
          <Chips
            value={formState.data.facilities}
            onChange={facilitiesOnChange}
            selectType="multiple"
            options={[
              { label: 'Мебель', value: 'Мебель' },
              { label: 'Техника для кухни', value: 'Техника для кухни' },
              { label: 'Стиральная машина', value: 'Стиральная машина' },
              { label: 'Интернет', value: 'Интернет' },
              { label: 'Лифт', value: 'Лифт' },
              { label: 'Пандус', value: 'Пандус' },
            ]}
          ></Chips>
        </DobroFormField>
      }
      availablePeriod={
        <DobroFormField name="DobroFormContainer-availablePeriod" label="На сколько готовы принять" required>
          <Chips
            value={formState.data.availablePeriod ? [formState.data.availablePeriod] : []}
            onChange={availablePeriodOnChange}
            options={[
              { label: 'Не знаю, обсудим', value: 'Не знаю, обсудим' },
              { label: 'Один месяц', value: 'Один месяц' },
              { label: 'Три месяца', value: 'Три месяца' },
              { label: 'Полгода и более', value: 'Полгода и более' },
            ]}
            invalid={formState.errors.invalidFields.includes('availablePeriod')}
          ></Chips>
        </DobroFormField>
      }
      priceType={
        <DobroFormField name="DobroFormContainer-priceType" label="На какой основе" required>
          <Chips
            value={formState.data.priceType ? [formState.data.priceType] : []}
            onChange={priceTypeOnChange}
            invalid={formState.errors.invalidFields.includes('priceType')}
            options={[
              { label: formatPriceType(EPriceType.Free), value: EPriceType.Free },
              { label: formatPriceType(EPriceType.Utility_bill), value: EPriceType.Utility_bill },
              { label: formatPriceType(EPriceType.Symbolic), value: EPriceType.Symbolic },
            ]}
          ></Chips>
        </DobroFormField>
      }
      priceDescription={
        priceDescriptionVisible(formState) && (
          <DobroFormField name="DobroFormContainer-priceDescription" label="Сумма или комментарий" required>
            <Input
              value={formState.data.priceDescription}
              onChange={priceDescriptionOnChange}
              size="M"
              invalid={formState.errors.invalidFields.includes('priceDescription')}
            />
          </DobroFormField>
        )
      }
      availableFor={
        <DobroFormField name="DobroFormContainer-availableFor" label="Могу принять">
          <Chips
            value={formState.data.availableFor}
            onChange={availableForOnChange}
            selectType="multiple"
            options={[
              { label: 'Семью с детьми', value: 'Семью с детьми' },
              { label: 'С животными', value: 'С животными' },
              { label: 'Маломобильных граждан', value: 'Маломобильных граждан' },
              { label: 'Пожилых', value: 'Пожилых' },
            ]}
          ></Chips>
        </DobroFormField>
      }
      otherWishes={
        <DobroFormField name="DobroFormContainer-otherWishes" label="Есть пожелания">
          <Textarea
            value={formState.data.otherWishes}
            onChange={otherWishesOnChange}
            placeholder="Расскажите, что вам важно"
            size="M"
          />
        </DobroFormField>
      }
      name={
        <DobroFormField name="DobroFormContainer-name" label="Имя и фамилия" required>
          <Input
            value={formState.data.name}
            onChange={nameOnChange}
            size="M"
            invalid={formState.errors.invalidFields.includes('name')}
          />
        </DobroFormField>
      }
      phone={
        <DobroFormField
          name="DobroFormContainer-phone"
          label="Телефон"
          required
          errorMessage={formState.errors.phoneErrorMessage}
        >
          <PhoneInput
            value={formState.data.phone}
            onChange={phoneOnChange}
            invalid={formState.errors.invalidFields.includes('phone')}
          />
        </DobroFormField>
      }
      additionalPhone={
        <DobroFormField
          name="DobroFormContainer-additionalPhone"
          label="Дополнительный телефон"
          errorMessage={formState.errors.additionalPhoneErrorMessage}
        >
          <PhoneInput
            value={formState.data.additionalPhone}
            onChange={additionalPhoneOnChange}
            invalid={formState.errors.invalidFields.includes('additionalPhone')}
          />
        </DobroFormField>
      }
      telegram={
        <DobroFormField name="DobroFormContainer-telegram" label="Ник в Telegram">
          <Input value={formState.data.telegram} onChange={telegramOnChange} placeholder="@" size="M" />
        </DobroFormField>
      }
      offerSigned={
        <DobroFormField name="DobroFormContainer-offerSigned">
          <DobroFormSign
            onOfferDocumentClick={onOfferDocumentClick}
            onPoliticsDocumentClick={onPoliticsDocumentClick}
            offerDocumentUrl={offerDocumentUrl}
            politicsDocumentUrl={politicsDocumentUrl}
            checked={formState.data.offerSigned}
            onChange={offerSignedOnChange}
            invalid={formState.errors.invalidFields.includes('offerSigned')}
          />
        </DobroFormField>
      }
    />
  );
}
